import { useEffect, useState, useMemo } from "react";
import api from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import CardDetailPanel from "@/components/board/CardDetailPanel";
import { exportCsv } from "@/lib/csv";
import { Archive, Search, FileDown } from "lucide-react";

export default function ArchivedPage() {
  const [cards, setCards] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [lists, setLists] = useState({});
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () =>
    Promise.all([
      api.get("/cards", { params: { status: "archived" } }),
      api.get("/templates"),
      api.get("/users"),
      api.get("/lists"),
    ]).then(([c, t, u, l]) => {
      setCards(c.data);
      setTemplates(t.data);
      setUsers(u.data);
      setLists(l.data);
    });

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return cards;
    return cards.filter((c) =>
      ((c.full_name || "") + " " + c.av_id + " " + (c.archived_reason || "") + " " + (c.payer_mco || ""))
        .toLowerCase().includes(s)
    );
  }, [cards, q]);

  const exportArchived = () => {
    exportCsv(
      `avida-archived-${new Date().toISOString().slice(0, 10)}.csv`,
      filtered.map((c) => ({
        av_id: c.av_id,
        full_name: c.full_name,
        stage_when_archived: c.current_stage,
        payer_mco: c.payer_mco,
        archived_at: c.archived_at,
        archived_reason: c.archived_reason,
        archived_note: c.archived_note,
      }))
    );
  };

  const tpl = (n) => templates.find((t) => t.stage_number === n);

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl w-full mx-auto">
        <div className="flex items-center gap-3 mb-1">
          <Archive className="h-6 w-6 text-[#FBBF24]" />
          <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">Archived</h1>
        </div>
        <p className="text-sm text-[#15233B]/60">Lost / withdrawn cards — kept for reporting and restoration.</p>

        <div className="mt-6 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#15233B]/40" />
            <input
              data-testid="archived-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search initials, ID, reason…"
              className="w-full pl-9 pr-3 py-2 border border-[#15233B]/15 rounded-md text-sm bg-white"
            />
          </div>
          <button
            onClick={exportArchived}
            data-testid="archived-csv-export"
            className="px-3 py-2 rounded-md border border-[#15233B]/15 hover:bg-[#15233B]/5 text-xs font-semibold flex items-center gap-1"
          >
            <FileDown className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>

        <div className="mt-6 bg-white border border-[#15233B]/10 rounded-lg shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F3EC] text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">
              <tr>
                <th className="text-left px-4 py-3">Card</th>
                <th className="text-left px-4 py-3">Stage when archived</th>
                <th className="text-left px-4 py-3">Reason</th>
                <th className="text-left px-4 py-3">When</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#15233B]/8">
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`archived-row-${c.av_id}`} className="hover:bg-[#F6F3EC]/50">
                  <td className="px-4 py-3">
                    <div className="font-bold text-[#15233B]">{c.full_name}</div>
                    <div className="text-xs font-mono text-[#15233B]/60">{c.av_id} · {c.payer_mco}</div>
                  </td>
                  <td className="px-4 py-3 text-[#15233B]/80 text-xs">
                    {c.current_stage} · {tpl(c.current_stage)?.name}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#15233B]">{c.archived_reason}</div>
                    <div className="text-xs text-[#15233B]/60 max-w-md truncate">{c.archived_note}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#15233B]/60">{c.archived_at && new Date(c.archived_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`archived-open-${c.av_id}`}
                      onClick={() => setSelected(c)}
                      className="text-xs font-semibold text-[#FBBF24] hover:underline"
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-xs italic text-[#15233B]/40">No archived cards.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <CardDetailPanel
          card={selected}
          template={tpl(selected.current_stage)}
          templates={templates}
          users={users}
          lists={lists}
          onClose={() => setSelected(null)}
          onChanged={(updated) => {
            if (updated.status === "active") {
              setCards((p) => p.filter((c) => c.id !== updated.id));
              setSelected(null);
            } else {
              setCards((p) => p.map((c) => (c.id === updated.id ? updated : c)));
              setSelected(updated);
            }
          }}
        />
      )}
      <BrandFooter />
    </div>
  );
}
